/**
 * GM Lobby Tools — live room roster + bot spawn/despawn/behavior, for
 * integration testing (DJ ask 2026-07-06: "spawn and adding a player to the
 * lobby... for testing integrations").
 *
 * UI over the proven `/bot` command surface (ChatCommandHandler.
 * handleBotCommand → BotManager). Bots are real Player schema entries, so a
 * spawned bot appearing in the roster below — and in the race-lobby card,
 * party lists, etc. — IS the integration signal.
 *
 * `/bot` is GM_HIGH (4); the local memory stack grants ADMIN (5) via
 * H2000_HARNESS_GM_LEVEL, and DJ's Twitch identity is ADMIN on PTR.
 */
import React, { useState } from 'react';
import {
  getRoom,
  sendSlashCommand,
  useRoomPulse,
  useSelfGmLevel,
  selfCanRun,
  GmLevelChip,
  gmBtnStyle,
  gmSectionTitleStyle,
} from './gmShared';

const CLASSES = ['warrior', 'mage', 'hunter', 'paladin', 'rogue', 'shaman', 'priest', 'warlock', 'druid'] as const;

interface RosterRow {
  sessionId: string;
  heroName: string;
  heroClass: string;
  level: number;
  gmLevel: number;
  isSelf: boolean;
}

function readRoster(): RosterRow[] {
  const room = getRoom();
  const rows: RosterRow[] = [];
  room?.state?.players?.forEach?.((p: any, sessionId: string) => {
    rows.push({
      sessionId,
      heroName: p.heroName ?? '(unnamed)',
      heroClass: p.heroClass ?? '?',
      level: p.level ?? 0,
      gmLevel: p.gmLevel ?? 0,
      isSelf: sessionId === room?.sessionId,
    });
  });
  return rows;
}

export const GmLobbyToolsContent: React.FC = () => {
  useRoomPulse();
  const gmLevel = useSelfGmLevel();
  const [botClass, setBotClass] = useState<string>('warrior');
  const [botCount, setBotCount] = useState(1);
  const roster = readRoster();
  const connected = !!getRoom();
  const canBot = connected && selfCanRun('bot', gmLevel);

  return (
    <div style={{ fontSize: 12, color: '#ecf0f1', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Room roster ({roster.length})</strong>
        <GmLevelChip gmLevel={gmLevel} />
      </div>
      {!connected && (
        <div style={{ opacity: 0.6, padding: '8px 0' }}>
          Not connected to a room — join any mode, then reopen. This panel drives the
          live Colyseus room (spawn bots, watch the roster) for integration testing.
        </div>
      )}

      <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid rgba(236,240,241,0.12)', borderRadius: 5 }}>
        {roster.map((r) => (
          <div
            key={r.sessionId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              padding: '3px 8px',
              borderBottom: '1px solid rgba(236,240,241,0.06)',
              background: r.isSelf ? 'rgba(46, 204, 113, 0.08)' : 'transparent',
              fontFamily: 'monospace',
              fontSize: 11,
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.isSelf ? '⭐ ' : ''}{r.heroName}
            </span>
            <span style={{ opacity: 0.7, flexShrink: 0 }}>
              {r.heroClass} L{r.level}{r.gmLevel > 0 ? ` · GM${r.gmLevel}` : ''}
            </span>
          </div>
        ))}
        {connected && roster.length === 0 && (
          <div style={{ padding: 8, opacity: 0.5 }}>No players in room state yet…</div>
        )}
      </div>

      <div style={gmSectionTitleStyle}>Spawn bots (/bot — GM 4+)</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          data-testid="gm-bot-class"
          value={botClass}
          onChange={(e) => setBotClass(e.target.value)}
          style={{ background: '#1a2230', color: '#ecf0f1', border: '1px solid rgba(236,240,241,0.25)', borderRadius: 5, padding: '4px 6px', fontSize: 11 }}
        >
          {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          data-testid="gm-bot-count"
          type="number"
          min={1}
          max={10}
          value={botCount}
          onChange={(e) => setBotCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
          style={{ width: 44, background: '#1a2230', color: '#ecf0f1', border: '1px solid rgba(236,240,241,0.25)', borderRadius: 5, padding: '4px 6px', fontSize: 11 }}
        />
        <button
          data-testid="gm-bot-spawn"
          style={gmBtnStyle(canBot)}
          disabled={!canBot}
          onClick={() => sendSlashCommand(`/bot spawn ${botClass} ${botCount}`)}
        >
          Spawn
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
        <button style={gmBtnStyle(canBot)} disabled={!canBot} onClick={() => sendSlashCommand('/bot despawn')}>Despawn nearest</button>
        <button style={gmBtnStyle(canBot)} disabled={!canBot} onClick={() => sendSlashCommand('/bot despawn all')}>Despawn all</button>
        <button style={gmBtnStyle(canBot)} disabled={!canBot} onClick={() => sendSlashCommand('/bot list')}>List → chat</button>
      </div>

      <div style={gmSectionTitleStyle}>Bot behavior</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {(['passive', 'wandering', 'aggressive'] as const).map((tier) => (
          <button key={tier} style={gmBtnStyle(canBot)} disabled={!canBot} onClick={() => sendSlashCommand(`/bot tier ${tier}`)}>
            {tier}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5 }}>
        Results echo to chat as system messages. Bots join room state like real
        players — check the race-lobby card / party lists to verify integrations.
      </div>
    </div>
  );
};
